// ./src/components/generic/CardListSkeleton/CardSkeletonParts.tsx
//
// The pieces of a record card's loading skeleton, for a list that draws its own card skeleton (an avatar, a badge row —
// a shape the generic `CardListSkeleton` cannot draw): the TABLET-ONLY buttons and checkbox, and the card's text ROWS.
//
// ⚠⚠ A tablet card (sm–md) carries its row buttons on the card, and usually a selection checkbox; a phone card carries
// neither (its actions are swipes, its checkbox exists only in selection mode). A card skeleton that leaves them out is
// right on a phone and wrong on a tablet: the card grows a column on the right — and on the left — the moment the data
// lands. ~30 lists shipped exactly that (owner, 2026-10-07, ROADMAP #110). So both pieces are `visibleFrom="sm"` here,
// once, and the number of circles is a REQUIRED number the list derives from the handlers it was given — the same
// handlers that decide which buttons its real card draws.
// ▶ npm run check-card-skeletons

import React from 'react';
import { Box, Group, Skeleton, Stack } from '@mantine/core';

/** What a list's card skeleton needs to know to be the shape of its tablet card. */
export interface CardSkeletonShape {
  /** How many row buttons the tablet card shows — derive it with `countCardActions(...)` from the list's handlers. */
  actions: number;
  /** Whether the tablet card shows a selection checkbox outside selection mode (the list's `isSelectable`). */
  withCheckbox: boolean;
}

/**
 * Counts the row buttons a card will draw: one per handler the list was given. Pass `true` for a button every
 * viewer gets (comments). Per-record rights are not known before the read, so a skeleton assumes them granted.
 */
export const countCardActions = (...handlers: unknown[]): number => handlers.filter(Boolean).length;

export interface CardSkeletonActionsProps {
  /** Number of 28px circles (an `ActionIcon size="md"` each). 0 draws nothing. */
  count: number;
  /** A card whose buttons sit in their own row under the content (Travel) aligns them to the end. */
  justify?: 'flex-end';
  /** The real button group's gap — 4 for a trailing column; a bottom row may use `xs`. */
  gap?: 4 | 'xs';
  /** `'all'` only for a button genuinely ON the phone card too (a picker's Add) — never a record's edit/remove. */
  on?: 'tablet' | 'all';
  /**
   * The buttons are TEXT buttons ("Add", "Mark paid"), not icons: their real height (a Mantine `sm` button is 36, `md` 42)
   * and about how wide they are. A 28px circle for a wide button was the wrong KIND of thing (#117).
   */
  button?: { height: 36 | 42; width: number };
}

/** The tablet card's row buttons. Hidden on a phone, where they are swipe actions (unless `on="all"`). */
export const CardSkeletonActions: React.FC<CardSkeletonActionsProps> = ({ count, justify, gap = 4, on = 'tablet', button }) => {
  if (count <= 0) {
    return null;
  }

  return (
    <Group gap={gap} wrap="nowrap" justify={justify} visibleFrom={on === 'tablet' ? 'sm' : undefined} style={{ flexShrink: 0 }}>
      {Array.from({ length: count }).map((_, i) =>
        button ? (
          <Skeleton key={i} height={button.height} width={button.width} radius="sm" />
        ) : (
          <Skeleton key={i} circle height={28} width={28} />
        ),
      )}
    </Group>
  );
};

/** The tablet card's leading selection checkbox. Hidden on a phone, which shows one only in selection mode. */
export const CardSkeletonCheckbox: React.FC<{ show: boolean }> = ({ show }) => {
  if (!show) {
    return null;
  }

  return (
    <Box visibleFrom="sm" style={{ flexShrink: 0 }}>
      <Skeleton height={20} width={20} radius="sm" />
    </Box>
  );
};

/** The height one row of a card takes — a line of `md` text (16px × Mantine's 1.55 line height for md). */
export const CARD_LINE_HEIGHT = 25;

/**
 * A row's text size → the height it takes: Mantine's font size × ITS line height for that size (xs 12 × 1.4, sm 14 × 1.45,
 * md 16 × 1.55, lg 18 × 1.6), rounded — the theme sets neither. ⚠ Not × 1.55 for all: that made every small row two or
 * three pixels too tall. ▶ check-card-skeletons computes them from Mantine's theme.
 */
export const CARD_ROW_HEIGHTS = {
  lg: 29,
  md: CARD_LINE_HEIGHT,
  sm: 20,
  xs: 17,
  // Not text: a row whose height is set by an `ActionIcon size="lg"` beside it (34px) — a contact's Call / Copy line.
  control: 34,
} as const;
export type CardRowSize = keyof typeof CARD_ROW_HEIGHTS;

/**
 * A row the real card draws DIFFERENTLY below and above `sm` (ROADMAP #127) — because the card itself switches there
 * (`hiddenFrom` / `visibleFrom="sm"`): a contact row that is a 34px Call / Copy row on a phone and a 25px link on a
 * tablet. `null` — no row at that width. Each width draws only its own, so the gap is counted once.
 * ⚠ NOT for a line that WRAPS: `sm` is 768px, so "phone" here is everything from 320 to 767px, and a header or a link
 * that wraps on a 375px phone fits on one line at 480. A wrap depends on the width, not the breakpoint — draw the
 * wrapping itself (the same flex-wrap of bars, or the text it wraps behind a `Skeleton`), as the comments and the
 * marketing board do.
 */
export interface CardSkeletonRowByWidth {
  phone: CardRowSize | null;
  tablet: CardRowSize | null;
}

/** One row of a card skeleton: a size, or a size per width. */
export type CardSkeletonRowSpec = CardRowSize | CardSkeletonRowByWidth;

// Varied so the block reads as text, not a bar chart; the first row is the title.
const LINE_WIDTHS = ['45%', '70%', '55%', '62%', '38%', '50%'];

export interface CardSkeletonLinesProps {
  /**
   * ⚠⚠ HOW MANY ROWS THE REAL CARD SHOWS — title, meta lines, a badge row each count one (ROADMAP #117). Two lines for a
   * card of four or five made every list grow taller the moment its data landed. REQUIRED, never defaulted: count the
   * rows the card draws for a typical record. (It is the number of entries in `sizes`: a `{ phone, tablet }` entry is one,
   * even where one side draws nothing.)
   */
  count: number;
  /**
   * The text size of each row, by position, when the card's rows are not all `md` — an `xs` date is 17px, an `lg` title
   * 29px, and a card of them drawn as 25px rows came out a dozen pixels too tall. A badge row: its badge size's height is
   * close to the `xs` / `sm` row (badges are 16 / 18 / 20px); a row held open by a large icon button is `control`. Rows
   * past the list's end are `md`. A row that differs between a phone and a tablet takes `{ phone, tablet }`.
   */
  sizes?: CardSkeletonRowSpec[];
  /** The real card's gap between those rows (its text `Stack`'s `gap`), when it is not 4px. */
  gap?: number | 'xs';
}

/** One row placeholder: a bar centred in a box as tall as the real row. */
const CardSkeletonRow: React.FC<{ index: number; size: CardRowSize; on?: 'phone' | 'tablet' }> = ({ index, size, on }) => {
  const rowHeight = CARD_ROW_HEIGHTS[size];

  return (
    <Box
      h={rowHeight}
      hiddenFrom={on === 'phone' ? 'sm' : undefined}
      visibleFrom={on === 'tablet' ? 'sm' : undefined}
      style={{ display: 'flex', alignItems: 'center' }}
    >
      <Skeleton height={Math.min(index === 0 ? 16 : 14, rowHeight - 6)} width={LINE_WIDTHS[index % LINE_WIDTHS.length]} />
    </Box>
  );
};

/**
 * A card's text rows: one placeholder per row, each a full row tall. A row given as `{ phone, tablet }` draws each width's
 * own (or nothing there); every other row is the same on both.
 * ⚠ It takes the width its PARENT gives it (`flex: 1`, bars in %): inside a box that only hugs its content it collapses
 * to 0px wide — the rows keep their height but draw no bars. Give such a parent a width (Travel's route did this).
 */
export const CardSkeletonLines: React.FC<CardSkeletonLinesProps> = ({ count, sizes = [], gap = 4 }) => (
  <Stack gap={gap} style={{ flex: 1, minWidth: 0 }}>
    {Array.from({ length: count }).map((_, i) => {
      const spec = sizes[i] ?? 'md';

      if (typeof spec === 'string') {
        return <CardSkeletonRow key={i} index={i} size={spec} />;
      }

      return (
        <React.Fragment key={i}>
          {spec.phone && <CardSkeletonRow index={i} size={spec.phone} on="phone" />}
          {spec.tablet && <CardSkeletonRow index={i} size={spec.tablet} on="tablet" />}
        </React.Fragment>
      );
    })}
  </Stack>
);
